/**
 * Shared newsletter state + form helpers.
 *
 * Three independent signals, all funnelled through this module so the
 * subscription popup and the image floating window never diverge:
 *   - subscribed       → long-lived cookie + localStorage (customer tags are
 *                        checked by each section via data-has-subscription)
 *   - popup dismissed  → today's date in its own cookie
 *   - float dismissed  → today's date in its own cookie
 * "Today" is compared as a YYYY-MM-DD value, not an expires timestamp, so a
 * natural-day rollover clears the dismissal without any timezone arithmetic.
 *
 * Design: forms submit NATIVELY (no fetch interception) so Shopify's invisible
 * CAPTCHA can attach its token. After a successful native POST, Shopify
 * redirects back with ?customer_posted=true; the promo popup detects that and
 * marks the visitor subscribed.
 */
window.MarstekNewsletter = (function () {
  const SUBSCRIBED_COOKIE = "marstek_newsletter_subscribed";
  const SUBSCRIBED_STORAGE_KEY = "marstek_newsletter_subscribed";
  const SUBSCRIBED_DAYS = 3650;
  const DISMISSAL_COOKIES = {
    popup: "marstek_popup_dismissed",
    float: "marstek_float_dismissed",
  };
  const DISMISSAL_COOKIE_DAYS = 2;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const POSTED_PARAMS = ["customer_posted", "contact_posted"];
  const STRIP_PARAMS = [...POSTED_PARAMS, "newsletter_test"];

  // Theme editor / visual preview: never read or write visitor state, so a
  // merchant can always see and re-test both surfaces.
  function isPreviewMode() {
    if (window.Shopify?.designMode || window.Shopify?.visualPreviewMode) return true;
    return Boolean(
      document.querySelector(
        "[data-newsletter-test-mode='true'], [data-test-mode='true']",
      ),
    );
  }

  function getCookie(name) {
    return document.cookie
      .split(";")
      .map((c) => c.trim().split("="))
      .filter(([n]) => n === name)
      .map(([, v]) => decodeURIComponent(v || ""))
      .find(Boolean);
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /** Local YYYY-MM-DD. Change here if the store's timezone should define "today". */
  function todayKey() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
  }

  /** True only when this surface was closed on the current natural day. */
  function hasDismissedToday(kind) {
    const name = DISMISSAL_COOKIES[kind];
    if (!name || isPreviewMode()) return false;
    return getCookie(name) === todayKey();
  }

  function markDismissedToday(kind) {
    const name = DISMISSAL_COOKIES[kind];
    if (!name || isPreviewMode()) return;
    setCookie(name, todayKey(), DISMISSAL_COOKIE_DAYS);
  }

  function isSubscribed() {
    if (isPreviewMode()) return false;
    if (getCookie(SUBSCRIBED_COOKIE) === "true") return true;
    try {
      return window.localStorage.getItem(SUBSCRIBED_STORAGE_KEY) === "true";
    } catch (error) {
      return false;
    }
  }

  function markSubscribed() {
    if (isPreviewMode()) return;
    setCookie(SUBSCRIBED_COOKIE, "true", SUBSCRIBED_DAYS);
    try {
      window.localStorage.setItem(SUBSCRIBED_STORAGE_KEY, "true");
    } catch (error) {
      // Storage can be blocked (private mode); the cookie still carries it.
    }
  }

  /** Backwards-compatible alias for older callers. */
  function hasSubscriptionCookie() {
    return isSubscribed();
  }

  /**
   * Client-side validation for a newsletter form.
   * @param {{email?:HTMLInputElement, consent?:HTMLInputElement}} fields
   * @returns {{ok:true,email:string}|{ok:false,message:string,field?:"email"|"consent"}}
   */
  function validate({ email, consent }) {
    const emailValue = (email?.value || "").trim();

    if (!emailValue) {
      return { ok: false, message: "Please enter your email address.", field: "email" };
    }

    if (!EMAIL_RE.test(emailValue)) {
      return { ok: false, message: "Invalid email address. Please enter a valid email address.", field: "email" };
    }

    if (consent && !consent.checked) {
      return { ok: false, message: "Please agree to the Privacy Policy before subscribing.", field: "consent" };
    }

    return { ok: true, email: emailValue };
  }

  /** Shopify's result flag from the redirect-back URL ("true" | "false" | null). */
  function getPostedFlag() {
    const params = new URLSearchParams(window.location.search);
    return POSTED_PARAMS.map((p) => params.get(p)).find(Boolean) || null;
  }

  /** True when the current page load is the redirect-back of a successful submit. */
  function hasJustPosted() {
    return getPostedFlag() === "true";
  }

  /** Strip posted/newsletter_test params so a reload/share won't re-trigger. */
  function cleanPostedUrl() {
    const url = new URL(window.location.href);
    let changed = false;
    STRIP_PARAMS.forEach((p) => {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        changed = true;
      }
    });
    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }
  }

  /**
   * Run on page load (from the site-wide promo popup). If the URL carries a posted
   * flag, act on it: success opens the promo popup + marks subscribed; failure just
   * cleans the URL (Shopify re-renders form.errors server-side).
   */
  function handlePostedOnLoad({ onSuccess, onFailure } = {}) {
    const flag = getPostedFlag();
    if (!flag) return false;

    cleanPostedUrl();

    if (flag === "true") {
      markSubscribed();
      window.dispatchEvent(new CustomEvent("newsletter:posted-success"));
      if (typeof onSuccess === "function") onSuccess();
    } else {
      window.dispatchEvent(new CustomEvent("newsletter:posted-failure"));
      if (typeof onFailure === "function") onFailure();
    }

    return true;
  }

  return {
    isPreviewMode,
    todayKey,
    hasDismissedToday,
    markDismissedToday,
    isSubscribed,
    markSubscribed,
    hasSubscriptionCookie,
    validate,
    getPostedFlag,
    hasJustPosted,
    cleanPostedUrl,
    handlePostedOnLoad,
  };
})();
